import {
  type CignaManagersResponse,
  CignaManagersResponse as CignaManagersResponseSchema,
} from '@uit/shared/schemas/rcf';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import { CancelOnboardingAlert } from '../components/onboarding/CancelOnboardingAlert.js';
import { MagicLinkPanel } from '../components/onboarding/MagicLinkPanel.js';
import { DeleteDocumentButton } from '../components/DeleteDocumentButton.js';
import { useApi } from '../lib/api.js';
import { useFeatureFlag } from '../lib/feature-flags.js';
import { DOC_LABELS } from '../lib/labels.js';
import s from './OnboardingDetailPage.module.scss';

const OnboardingDocument = z.object({
  id: z.string(),
  kind: z.string(),
  original_filename: z.string(),
  size_bytes: z.number(),
  created_at: z.string(),
  download_url: z.string(),
});

const EsignDocumentStatus = z.object({
  status: z.string(),
  signers: z.array(
    z.object({
      name: z.string().nullable(),
      email: z.string().nullable(),
      status: z.string().nullable(),
      order: z.number().nullable(),
      last_activity_at: z.string().nullable(),
    }),
  ),
});

const OnboardingEmailDelivery = z.object({
  id: z.string(),
  to_address: z.string(),
  subject: z.string().nullable(),
  status: z.string(),
  detail: z.string().nullable(),
  created_at: z.string(),
});

// Permissive: rate_to_client present for an owner, omitted otherwise (T-186).
const OnboardingDetail = z.object({
  id: z.string().uuid(),
  state: z.string(),
  admin_resource_name: z.string(),
  admin_business_email: z.string(),
  admin_role: z.string().nullable(),
  rate_to_contractor: z.number(),
  rate_to_client: z.number().nullable().optional(),
  client: z.object({
    id: z.string().uuid(),
    name: z.string(),
    project_or_product_name: z.string(),
  }),
  start_date: z.string(),
  end_date: z.string(),
  product_service: z.string(),
  esign_envelope_id: z.string().nullable().optional(),
  // True when a real BoldSign provider is live → hide the manual blind-advance
  // buttons; the webhook drives signing (T-133). Absent/false → dev/stub.
  esign_provider_active: z.boolean().optional(),
  cigna_manager_id: z.string().uuid().nullable().optional(),
  magic_link_expires_at: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  // Actually-uploaded documents with short-lived signed download URLs (T-134).
  documents: z.array(OnboardingDocument).optional(),
  // Live BoldSign status for in-flight onboardings; null when unavailable (T-134).
  esign_status: EsignDocumentStatus.nullable().optional(),
  // Per-message Resend delivery status — surfaces a bounced invite/welcome (T-139).
  email_deliveries: z.array(OnboardingEmailDelivery).optional(),
});
type OnboardingDetail = z.infer<typeof OnboardingDetail>;

const STATE_LABELS: Record<string, string> = {
  pending_contractor_input: 'Waiting on vendor',
  pending_contract_signature: 'Waiting on vendor signature',
  pending_admin_signature: 'Waiting on admin signature',
  active: 'Active',
  expired: 'Expired',
  cancelled: 'Cancelled',
  draft: 'Draft',
};

function badgeClass(state: string): string | undefined {
  if (state === 'cancelled' || state === 'expired') return s.badgeMuted;
  if (state === 'active') return s.badgeSuccess;
  return s.badgePending;
}

function stateLabel(state: string): string {
  return STATE_LABELS[state] ?? state;
}

function formatUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const DELIVERY_LABELS: Record<string, string> = {
  sent: 'Sent',
  delivered: 'Delivered',
  bounced: 'Bounced',
  complained: 'Spam complaint',
  delivery_delayed: 'Delayed',
};

function isDeliveryFailure(status: string): boolean {
  return status === 'bounced' || status === 'complained';
}

// Map the RCF send route's blocked reasons (generateRcf) to admin-facing text.
function rcfErrorText(status: number, error?: string): string {
  if (status === 409 || error === 'already_sent') return 'RCF already sent for this onboarding.';
  switch (error) {
    case 'no_manager':
      return 'Select the Cigna manager first.';
    case 'manager_no_principal':
      return 'That manager has no principal set — fix it in Settings → Cigna managers.';
    case 'missing_settings':
      return 'Set the Company Code + Location address in Settings → RCF first.';
    case 'no_uit_email':
      return 'The uIT mailbox has not been created yet for this resource.';
    default:
      return `Could not send the RCF (${status}).`;
  }
}

export function OnboardingDetailPage() {
  const { id } = useParams<{ id: string }>();
  // RCF review-and-send is gated by the rcf feature flag (T-152).
  const rcfEnabled = useFeatureFlag('rcf');
  const { data, error, loading, refetch } = useApi<OnboardingDetail>(
    id ? `/api/onboardings/${id}` : null,
    OnboardingDetail,
  );
  const { data: managers } = useApi<CignaManagersResponse>(
    id ? '/api/settings/cigna-managers' : null,
    CignaManagersResponseSchema,
  );

  const [cancelOpen, setCancelOpen] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendLink, setResendLink] = useState<string | null>(null);
  const [esignBusy, setEsignBusy] = useState(false);
  const [esignError, setEsignError] = useState<string | null>(null);
  const [rcfManagerId, setRcfManagerId] = useState('');
  const [rcfBusy, setRcfBusy] = useState(false);
  const [rcfMsg, setRcfMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function advanceEsign(action: 'contractor-signed' | 'countersigned'): Promise<void> {
    if (!id) return;
    setEsignBusy(true);
    setEsignError(null);
    try {
      const res = await fetch(`/api/onboardings/${id}/esign/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setEsignError(
          res.status === 409
            ? 'This onboarding is not in the expected state — refresh and try again.'
            : `Could not advance (${res.status}).`,
        );
        return;
      }
      void refetch();
    } catch (err) {
      setEsignError(err instanceof Error ? err.message : String(err));
    } finally {
      setEsignBusy(false);
    }
  }

  async function sendRcf(): Promise<void> {
    if (!id) return;
    setRcfBusy(true);
    setRcfMsg(null);
    try {
      const res = await fetch(`/api/onboardings/${id}/rcf/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(rcfManagerId ? { cigna_manager_id: rcfManagerId } : {}),
      });
      if (res.ok) {
        const body = (await res.json().catch(() => ({}))) as { recipients?: string[] };
        setRcfMsg({ kind: 'ok', text: `RCF sent to ${body.recipients?.length ?? 0} recipients.` });
        void refetch();
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setRcfMsg({ kind: 'err', text: rcfErrorText(res.status, body.error) });
    } catch (err) {
      setRcfMsg({ kind: 'err', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setRcfBusy(false);
    }
  }

  async function handleResend(): Promise<void> {
    if (!id) return;
    setResendStatus('sending');
    setResendError(null);
    try {
      const res = await fetch(`/api/onboardings/${id}/resend-link`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 409) {
          setResendError("Can't resend — onboarding is cancelled or already active.");
        } else {
          setResendError(`${res.status} ${res.statusText}`);
        }
        setResendStatus('error');
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { magic_link_url?: string };
      setResendLink(body.magic_link_url ?? null);
      setResendStatus('sent');
      void refetch();
    } catch (err) {
      setResendError(err instanceof Error ? err.message : String(err));
      setResendStatus('error');
    }
  }

  if (loading && !data) return <div className={s.loading}>Loading…</div>;
  if (error || !data) {
    const is404 = error?.message.includes('404');
    return (
      <div className={s.notFound}>
        <Link to="/onboarding" className={s.back}>
          ← Back to queue
        </Link>
        <div className={s.notFoundBody}>
          {is404 ? 'Onboarding not found.' : `Failed to load: ${error?.message ?? 'unknown'}`}
        </div>
      </div>
    );
  }

  const blockedForResendOrCancel = data.state === 'cancelled' || data.state === 'active';
  const canCancel = data.state !== 'cancelled' && data.state !== 'active';
  const expiresAt = new Date(data.magic_link_expires_at);

  return (
    <div className={s.page}>
      <Link to="/onboarding" className={s.back}>
        ← Back to queue
      </Link>

      <div className={s.header}>
        <div>
          <h1 className={s.title}>{data.admin_resource_name}</h1>
          <p className={s.subtitle}>
            {data.client.name} · {data.client.project_or_product_name}
          </p>
        </div>
        <span className={badgeClass(data.state)}>{stateLabel(data.state)}</span>
      </div>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Admin-entered details</h2>
        <dl className={s.grid}>
          <div>
            <dt>Business email</dt>
            <dd>{data.admin_business_email}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd>{data.admin_role ?? <span className={s.muted}>(none)</span>}</dd>
          </div>
          <div>
            <dt>Start date</dt>
            <dd>{data.start_date}</dd>
          </div>
          <div>
            <dt>End date</dt>
            <dd>{data.end_date}</dd>
          </div>
          <div>
            <dt>Rate to vendor</dt>
            <dd>{formatUsd(data.rate_to_contractor)}</dd>
          </div>
          {data.rate_to_client !== undefined && (
            <div>
              <dt>Rate to client</dt>
              <dd>
                {data.rate_to_client == null ? (
                  <span className={s.muted}>(not set)</span>
                ) : (
                  formatUsd(data.rate_to_client)
                )}
              </dd>
            </div>
          )}
          <div className={s.gridSpan}>
            <dt>Product / service</dt>
            <dd>{data.product_service}</dd>
          </div>
        </dl>
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Magic link</h2>
        <p className={s.linkInfo}>
          Expires <strong>{expiresAt.toLocaleString()}</strong>. Email is stubbed in this build —
          use <strong>Resend link</strong> to generate a fresh link you can copy and send to the
          vendor. (The token isn&apos;t stored in cleartext, so it can only be shown right after
          it&apos;s minted.)
        </p>
        <div className={s.linkRow}>
          <button
            type="button"
            className={s.btn}
            onClick={() => {
              void handleResend();
            }}
            disabled={blockedForResendOrCancel || resendStatus === 'sending'}
          >
            {resendStatus === 'sending' ? 'Sending…' : 'Resend link'}
          </button>
        </div>
        {resendStatus === 'sent' && resendLink && <MagicLinkPanel url={resendLink} />}
        {resendStatus === 'error' && resendError && (
          <div className={s.errorMsg} role="alert">
            {resendError}
          </div>
        )}
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Documents</h2>
        {data.documents && data.documents.length > 0 ? (
          <ul className={s.docList}>
            {data.documents.map((doc) => (
              <li key={doc.id} className={s.docRow}>
                <span className={s.docKind}>{DOC_LABELS[doc.kind] ?? doc.kind}</span>
                <a
                  className={s.docLink}
                  href={doc.download_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {doc.original_filename}
                </a>
                <span className={s.docMeta}>
                  {formatBytes(doc.size_bytes)} · {new Date(doc.created_at).toLocaleDateString()}
                </span>
                <DeleteDocumentButton
                  id={doc.id}
                  filename={doc.original_filename}
                  onDeleted={() => void refetch()}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className={s.muted}>
            No documents uploaded yet — vendor uploads appear here once they open the link and
            submit.
          </p>
        )}
      </section>

      {/* Email delivery (T-139): surfaces a bounced invite / welcome email so the
          admin can fix the address and resend, instead of a silent loss. */}
      {data.email_deliveries && data.email_deliveries.length > 0 && (
        <section className={s.section}>
          <h2 className={s.sectionTitle}>Email delivery</h2>
          <ul className={s.docList}>
            {data.email_deliveries.map((d) => (
              <li key={d.id} className={s.deliveryRow}>
                <span className={isDeliveryFailure(d.status) ? s.deliveryBad : s.deliveryOk}>
                  {DELIVERY_LABELS[d.status] ?? d.status}
                </span>
                <span className={s.deliveryWho}>
                  <span>{d.subject ?? '(email)'}</span>
                  <span className={s.signerEmail}>{d.to_address}</span>
                  {d.detail && isDeliveryFailure(d.status) && (
                    <span className={s.deliveryDetail}>{d.detail}</span>
                  )}
                </span>
                <span className={s.docMeta}>{new Date(d.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rcfEnabled && data.state !== 'cancelled' && data.state !== 'active' && (
        <section className={s.section}>
          <h2 className={s.sectionTitle}>Resource Change Form (Cigna)</h2>
          <p className={s.muted}>
            Pick the Cigna manager, then send the RCF to Cigna with the workbook attached — sent
            pre-submit so Cigna can order the laptop before the start date.
          </p>
          <label
            htmlFor="rcf-manager"
            style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 4 }}
          >
            Cigna manager
          </label>
          <select
            id="rcf-manager"
            value={rcfManagerId || (data.cigna_manager_id ?? '')}
            onChange={(e) => setRcfManagerId(e.target.value)}
            style={{
              display: 'block',
              width: '100%',
              maxWidth: 440,
              padding: '8px 10px',
              margin: '0 0 12px',
              border: '1px solid #d4d4d8',
              borderRadius: 4,
              fontSize: 14,
            }}
          >
            <option value="">— Select the manager this resource reports to —</option>
            {managers?.managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} — {m.email}
              </option>
            ))}
          </select>
          <button type="button" className={s.btn} disabled={rcfBusy} onClick={() => void sendRcf()}>
            {rcfBusy ? 'Sending…' : 'Send RCF to Cigna'}
          </button>
          {rcfMsg && (
            <div
              className={rcfMsg.kind === 'ok' ? s.successMsg : s.errorMsg}
              role={rcfMsg.kind === 'ok' ? 'status' : 'alert'}
              style={{ marginTop: 12 }}
            >
              {rcfMsg.text}
            </div>
          )}
        </section>
      )}

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Contract signing</h2>
        {data.esign_envelope_id && (
          <p className={s.linkInfo}>
            Envelope: <code className={s.code}>{data.esign_envelope_id}</code>
          </p>
        )}

        {/* Live BoldSign signer progress so a normal "awaiting the contractor"
            is legible in-portal (T-134). Null when unavailable (dev/stub/fetch). */}
        {data.esign_status && (
          <div className={s.esignStatus}>
            <p className={s.esignStatusHead}>
              BoldSign status: <strong>{data.esign_status.status}</strong>
            </p>
            <ul className={s.signerList}>
              {data.esign_status.signers.map((sg, i) => (
                <li key={`${sg.email ?? sg.name ?? 'signer'}-${i}`} className={s.signerRow}>
                  <span className={s.signerOrder}>{sg.order ?? i + 1}</span>
                  <span className={s.signerWho}>
                    {sg.name ?? '—'}
                    {sg.email && <span className={s.signerEmail}>{sg.email}</span>}
                  </span>
                  <span className={s.signerState}>{sg.status ?? '—'}</span>
                  <span className={s.signerActivity}>
                    {sg.last_activity_at
                      ? new Date(sg.last_activity_at).toLocaleString()
                      : '—'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.esign_provider_active ? (
          // Real provider (BoldSign): signing happens in BoldSign and advances via
          // the webhook — no manual blind-advance (T-133).
          data.state === 'active' ? (
            <p className={s.successMsg} role="status">
              Both parties signed — engagement is active.
            </p>
          ) : (
            <p className={s.muted}>
              Signatures are handled in BoldSign. This page updates automatically once both parties
              sign — there is no manual advance.
            </p>
          )
        ) : (
          // Dev/test: no real e-sign provider, so advance the loop by hand.
          <>
            <p className={s.muted}>
              No e-sign provider is configured (dev/test) — advance the signature steps manually to
              complete the loop.
            </p>
            {data.state === 'pending_contract_signature' && (
              <button
                type="button"
                className={s.btn}
                onClick={() => {
                  void advanceEsign('contractor-signed');
                }}
                disabled={esignBusy}
              >
                {esignBusy ? 'Working…' : 'Mark contract signed by vendor'}
              </button>
            )}
            {data.state === 'pending_admin_signature' && (
              <button
                type="button"
                className={s.btn}
                onClick={() => {
                  void advanceEsign('countersigned');
                }}
                disabled={esignBusy}
              >
                {esignBusy ? 'Working…' : 'Mark as countersigned & activate'}
              </button>
            )}
            {data.state === 'active' && (
              <p className={s.successMsg} role="status">
                Both parties signed — engagement is active.
              </p>
            )}
            {data.state !== 'pending_contract_signature' &&
              data.state !== 'pending_admin_signature' &&
              data.state !== 'active' && (
                <p className={s.muted}>
                  Signing actions become available once the contractor submits.
                </p>
              )}
          </>
        )}
        {esignError && (
          <div className={s.errorMsg} role="alert">
            {esignError}
          </div>
        )}
      </section>

      {canCancel && (
        <div className={s.dangerZone}>
          <button
            type="button"
            className={s.dangerBtn}
            onClick={() => {
              setCancelOpen(true);
            }}
          >
            Cancel onboarding
          </button>
        </div>
      )}

      <CancelOnboardingAlert
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onboardingId={data.id}
        contractorName={data.admin_resource_name}
        onCancelled={() => {
          void refetch();
        }}
      />
    </div>
  );
}
